import { assign, createMachine, sendParent, spawn } from 'xstate';
import isDCHEligible from '../../helpers/isDCHEligible';
import { CHECKING, PINLESS_DEBIT_CARD } from '../../const/paymentMedium';
import { getDefaultPaymentMethodFromPaymentEnterpriseEligibility } from '../../helpers';
import createBankFormMachine from './bankForm/createBankFormMachine';
import createCardFormMachine from './cardForm/createCardFormMachine';
import createDCHDebitFormMachine from './cardForm/createDCHDebitFormMachine';
export default createMachine({
    predictableActionArguments: true,
    id: 'addPaymentMethodMachine',
    schema: {
        events: {}
    },
    context: {
        apiMachineRef: undefined,
        form: undefined
    },
    initial: 'initializing',
    states: {
        initializing: {
            always: [
                {
                    target: 'addNewCheckingAccount',
                    cond: {
                        type: 'isDefaultPaymentMethod',
                        paymentMethod: CHECKING
                    }
                },
                {
                    target: 'addNewDebitCard',
                    cond: {
                        type: 'isDefaultPaymentMethod',
                        paymentMethod: PINLESS_DEBIT_CARD
                    }
                }
            ]
        },
        addNewCheckingAccount: {
            entry: assign({
                form({ apiMachineRef, form }) {
                    if (form) {
                        form.stop();
                    }
                    return spawn(createBankFormMachine(apiMachineRef), {
                        name: 'bankFormMachine'
                    });
                }
            })
        },
        addNewSavingsAccount: {
            entry: assign({
                form({ apiMachineRef, form }) {
                    if (form) {
                        form.stop();
                    }
                    return spawn(createBankFormMachine(apiMachineRef), {
                        name: 'bankFormMachine'
                    });
                }
            })
        },
        addNewDebitCard: {
            entry: assign({
                form({ form, apiMachineRef }) {
                    if (form) {
                        form.stop();
                    }
                    const { accountSummaryQueryResult: { data: accountSummary }, featureFlags } = apiMachineRef.getSnapshot().context;
                    const isDCHDebitCard = isDCHEligible(accountSummary.billing, accountSummary.product.type, featureFlags);
                    if (isDCHDebitCard) {
                        return spawn(createDCHDebitFormMachine(), {
                            name: 'cardFormMachine'
                        });
                    }
                    return spawn(createCardFormMachine(), {
                        name: 'cardFormMachine'
                    });
                }
            })
        },
        addNewCreditCard: {
            entry: assign({
                form({ form }) {
                    if (form) {
                        form.stop();
                    }
                    return spawn(createCardFormMachine(), {
                        name: 'cardFormMachine'
                    });
                }
            })
        }
    },
    on: {
        GO_TO_CHECKING: {
            target: 'addNewCheckingAccount'
        },
        GO_TO_SAVINGS: {
            target: 'addNewSavingsAccount'
        },
        GO_TO_DEBIT: {
            target: 'addNewDebitCard'
        },
        GO_TO_CREDIT: {
            target: 'addNewCreditCard'
        },
        CANCEL: {
            actions: sendParent({ type: 'CANCEL' })
        }
    }
}, {
    guards: {
        isFormValid: ({ form }) => {
            return form.getSnapshot().matches('validation.valid');
        },
        isDefaultPaymentMethod: ({ apiMachineRef }, event, { cond }) => {
            /*
             * we are using this `cond` key as expected from the xstate docs, but typescript isnt behaving
             * with the given type so we are casting it in the function itself
             * link to custom guards documentation: https://xstate.js.org/docs/guides/guards.html#custom-guards
             */
            const { paymentMethod } = cond;
            const { accountSummaryQueryResult: { data: accountSummary }, paymentEnterpriseEligibilityQueryResult: { data: paymentEnterpriseEligibility } } = apiMachineRef.getSnapshot().context;
            const productType = accountSummary.product.type;
            const defaultPaymentMethod = getDefaultPaymentMethodFromPaymentEnterpriseEligibility(productType, paymentEnterpriseEligibility);
            return defaultPaymentMethod === paymentMethod;
        }
    }
});
